import React, { lazy, Suspense, useEffect, useState } from "react";
import "tailwindcss/tailwind.css";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  getTodaysTasks,
  getTaskRemark,
  addTaskRemark,
  updateTask,
  getPendingTasks,
} from "../../store/slices/TaskSlice";
import Button, { ButtonType } from "../../components/Assets/Button";
import { formatFollowUpDate } from "../../FormatedDate";
import { toast } from "react-hot-toast";
import dayjs, { Dayjs } from "dayjs";
import Loading from "../../components/AssetsComponent/loading";
import { Info } from "lucide-react";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import PendingTask from "./PendingTask";
import TaskDetailsModal from "./TaskDetailsModal";
import { ITaskData, ITaskRemark } from "../../models/TaskData.model";
import { fetchUserNames } from "../../store/slices/userSlice";
import Select, { SingleValue } from "react-select";

dayjs.extend(utc);
dayjs.extend(timezone);

const CompletedTask = lazy(
  () => import("../LeadDetails/CompletedTask/CompletedTask")
);

const NotificationPage: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const [taskRemarks, setTaskRemarks] = useState<{ [key: string]: string }>({});
  const [newFollowUpTime, setNewFollowUpTime] = useState<Dayjs | null>(null);
  const [newFollowUpDate, setNewFollowUpDate] = useState<Dayjs | null>(null);
  const [completeChecked, setCompleteChecked] = useState(false);
  const { task, loading } = useSelector((state: RootState) => state.task);
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<ITaskData | null>(null);

  const { allUsersNames } = useSelector((state: RootState) => state.users);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const userOptions = allUsersNames.map((user) => ({
    label: user.username,
    value: user._id,
  }));

  type UserOption = {
    label: string;
    value: string;
  };

  // Modify the details button click handler
  const handleDetailsClick = (taskId: string) => {
    const tasks = task.find((t: ITaskData) => t._id === taskId);
    setSelectedTask(tasks ?? null);
    setOpenDoneTaskId(taskId);
    setIsTaskOpen(true);
    getRemarks(taskId);
  };

  const [openDoneTaskId, setOpenDoneTaskId] = useState<string | null>(null);
  const [date, setDate] = useState<string>("");
  const [isTaskOpen, setIsTaskOpen] = useState(false);
  const [remarks, setRemarks] = useState<ITaskRemark[]>([]);

  const dateString = date.split("-").reverse().join("/");

  const handleRemarkChange = (taskId: string, value: string) => {
    setTaskRemarks((prev) => ({ ...prev, [taskId]: value }));
  };

  useEffect(() => {
    dispatch(fetchUserNames());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getTodaysTasks({
        date: dateString || undefined,
        assignee: selectedUserId || undefined,
      })
    );
  }, [dateString, selectedUserId, dispatch]);

  const handleDoneClick = async (taskId: string) => {
    const remark = taskRemarks[taskId];
    if (!remark || remark.trim() === "") {
      return toast.error(
        "Please add a remark before marking this task as done."
      );
    }

    try {
      await dispatch(
        updateTask({
          _id: taskId,
          action: "done",
        })
      );

      dispatch({
        type: "tasks/handleTaskCompletion",
        payload: { _id: taskId },
      });

      await handleAddRemark(taskId, remark);
      await dispatch(getPendingTasks(pendingUserId));
      await dispatch(
        getTodaysTasks({
          date: dateString || undefined,
          assignee: selectedUserId || undefined,
        })
      );
      toast.success("Task marked as done successfully.");
      // Clear the remark and close the modal.
      setTaskRemarks((prev) => ({ ...prev, [taskId]: "" }));
      setIsTaskOpen(false);
      setCompleteChecked(false);
    } catch (error) {
      toast.error("An error occurred while marking the task as done.");
      console.error("Error marking task as done:", error);
    }
  };

  const handleAddRemark = async (taskId: string, remark: string) => {
    if (!taskId || !remark) {
      console.error("Task ID and remark are required");
      return;
    }

    try {
      await dispatch(
        addTaskRemark({ taskId, remark: { text: remark } })
      )?.unwrap();
      const updatedRemarks = await getRemarks(taskId);
      setTaskRemarks((prev) => ({ ...prev, [taskId]: "" }));
      return updatedRemarks;
    } catch (error) {
      console.error("Error adding remark:", error);
      return null;
    }
  };

  async function getRemarks(leadId: string) {
    try {
      const response = await dispatch(getTaskRemark({ leadId })).unwrap();
      if (response && response.data) {
        setRemarks(response.data);
      } else {
        setRemarks([]);
      }
    } catch (error) {
      setRemarks([]);
    }
  }

  const handleUpdateTaskFollowUp = async (_id: string) => {
    const remark = taskRemarks[_id];
    if (!remark) {
      toast.error("Please add a remark before postponing the task.");
      return;
    }

    const getCombinedDateTime = (
      newFollowUpDate: Dayjs | null,
      newFollowUpTime: Dayjs | null
    ): string | null => {
      if (!newFollowUpDate || !newFollowUpTime) {
        return null;
      }
      const combinedIST = dayjs(newFollowUpDate)
        .tz("Asia/Kolkata")
        .hour(newFollowUpTime.hour())
        .minute(newFollowUpTime.minute())
        .second(0)
        .millisecond(0);
      return combinedIST.utc().toISOString();
    };

    const formatedDate = getCombinedDateTime(newFollowUpDate, newFollowUpTime);
    if (!formatedDate) {
      return toast.error("Please provide a valid date and time.");
    }

    try {
      setNewFollowUpDate(null);
      setNewFollowUpTime(null);
      await dispatch(
        updateTask({
          _id,
          action: "postpone",
          followUp: formatedDate,
        })
      ).unwrap();
      await handleAddRemark(_id, remark);
      await dispatch(getPendingTasks(pendingUserId));
      await dispatch(
        getTodaysTasks({
          date: dateString || undefined,
          assignee: selectedUserId || undefined,
        })
      );
      toast.success("Task postponed successfully.");
      setTaskRemarks((prev) => ({ ...prev, [_id]: "" }));
    } catch (error: any) {
      toast.error(error.message || "An unexpected error occurred");
    }
  };

  const handleAddRemarkOrAction = (taskId: string) => {
    const remark = taskRemarks[taskId];
    if (!remark || remark.trim() === "") {
      return toast.error("Remark is required.");
    }
    if (newFollowUpDate && newFollowUpTime) {
      return handleUpdateTaskFollowUp(taskId);
    }
    if (completeChecked) {
      return handleDoneClick(taskId);
    }
    return handleAddRemark(taskId, remark);
  };

  if (!task) {
    return null;
  }

  if (loading && task.length === 0) {
    return <Loading />;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <PendingTask
        getRemarks={getRemarks}
        setOpenDoneTaskId={setOpenDoneTaskId}
        setIsTaskOpen={setIsTaskOpen}
        isTaskOpen={isTaskOpen}
        openDoneTaskId={openDoneTaskId}
        remarks={remarks}
        newFollowUpDate={newFollowUpDate}
        newFollowUpTime={newFollowUpTime}
        setNewFollowUpDate={setNewFollowUpDate}
        setNewFollowUpTime={setNewFollowUpTime}
        completeChecked={completeChecked}
        setCompleteChecked={setCompleteChecked}
        handleAddRemarkOrAction={handleAddRemarkOrAction}
        taskRemarks={taskRemarks}
        handleRemarkChange={handleRemarkChange}
        selectedUserId={pendingUserId}
        setSelectedUserId={setPendingUserId}
      />

      <div className="bg-white shadow-lg rounded-lg p-6">
        <div className="mb-4 flex items-center gap-4 w-fit">
          <input
            type="date"
            className="w-40 text-sm border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <div className="w-64">
            <Select
              options={userOptions}
              value={
                userOptions.find((opt) => opt.value === selectedUserId) || null
              }
              onChange={(option: SingleValue<UserOption>) =>
                setSelectedUserId(option?.value ?? null)
              }
              placeholder="Select Assignee..."
              isClearable
              className="text-sm"
            />
          </div>
        </div>{" "}
        <h1 className="text-lg font-semibold text-slate-800 flex items-center mb-4">
          <span className="w-1 h-6 bg-navyBlue-primary mr-3 rounded-full" />
          {date ? `Tasks for ${dateString}` : "Today's Tasks"}
        </h1>
        <ul className="space-y-4">
          {Array.isArray(task) && task.length > 0 ? (
            task
              .filter((task) => !task.done)
              .map((task: ITaskData, index: number) => {
                return (
                  <li
                    key={index}
                    className="flex items-center justify-between p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-600 hover:bg-blue-50 hover:shadow-xl transition-all duration-200"
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-semibold text-blue-600">
                        {index + 1}.
                      </span>
                      <div className="flex flex-col sm:flex-row justify-between items-start">
                        <div className="flex flex-col mr-4">
                          <p className="text-blue-600 font-medium py-1 rounded-full text-xs">
                            {task.followUp
                              ? formatFollowUpDate(task?.followUp as string)
                              : "00 Hr"}
                          </p>
                          <h2 className="text-sm md:text-base font-medium text-gray-800">
                            {task.taskSummary}
                          </h2>
                        </div>
                        <div className="flex flex-col mt-2 sm:mt-0 gap-2">
                          <p className="text-xs text-gray-600 mt-1 mb-1">
                            <strong>Name:</strong> {task?.lead?.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            <strong>Mobile:</strong> {task?.lead?.mobileNumber}
                          </p>
                          <p className="text-xs text-blue-600">
                            Assignee:{" "}
                            {(task.assignee as any)?.username || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div>
                      <Button
                        onClick={() => handleDetailsClick(task._id as string)}
                        type={ButtonType.Submit}
                        className="flex items-center gap-2 bg-transparent md:bg-navyBlue-primary"
                      >
                        <span className="hidden md:inline">Details</span>
                        <span className="block md:hidden">
                          <Info className="w-5 h-5 text-green-600" />
                        </span>
                      </Button>
                    </div>

                    {isTaskOpen &&
                      openDoneTaskId === task?._id &&
                      selectedTask && (
                        <TaskDetailsModal
                          isOpen={isTaskOpen && !!openDoneTaskId}
                          onClose={() => {
                            setIsTaskOpen(false);
                            setOpenDoneTaskId(null);
                            setSelectedTask(null);
                          }}
                          task={selectedTask}
                          remarks={remarks}
                          newFollowUpDate={newFollowUpDate}
                          newFollowUpTime={newFollowUpTime}
                          setNewFollowUpDate={setNewFollowUpDate}
                          setNewFollowUpTime={setNewFollowUpTime}
                          completeChecked={completeChecked}
                          setCompleteChecked={setCompleteChecked}
                          onAddRemark={() =>
                            handleAddRemarkOrAction(openDoneTaskId!)
                          }
                          taskRemarks={taskRemarks[openDoneTaskId || ""] || ""}
                          onRemarkChange={(value) =>
                            handleRemarkChange(openDoneTaskId!, value)
                          }
                        />
                      )}
                  </li>
                );
              })
          ) : (
            <p className="text-gray-600">No tasks found</p>
          )}
        </ul>
      </div>
      <Suspense fallback={<Loading />}>
        <CompletedTask />
      </Suspense>
    </div>
  );
};

export default NotificationPage;
